const { getElement } = window.BAO.Utils

class PushCartError {
  /**
   * @param {Object} error
   *
   * @returns {void}
   */
  static async handle (error) {
    const instance = new this(error)

    if (instance.genericError) {
      return instance.render(instance.MESSAGES.GENERIC)
    } else if (instance.soldOut) {
      return instance.render(instance.MESSAGES.SOLD_OUT)
    } else if (instance.alreadyHaveAllInCart) {
      return instance.render(instance.MESSAGES.ALREADY_HAVE_ALL_STOCK)
    } else if (instance.addedMoreThanExists) {
      return instance.render(instance.MESSAGES.NOT_ENOUGH_IN_STOCK)
    } else if (instance.cannotFindVariant) {
      return instance.render(instance.MESSAGES.CANNOT_FIND_VARIANT)
    } else {
      if (typeof error?.message === 'string') {
        return instance.render(error.message)
      } else if (typeof error?.errors === 'string') {
        return instance.render(error.errors)
      }

      return instance.render(instance.MESSAGES.ADD_TOO_MANY)
    }
  }

  constructor (error) {
    this.error = error

    this.MESSAGES = {
      ADD_TOO_MANY: `Unfortunately, there isn't enough of this product available`,
      ALREADY_HAVE_ALL_STOCK: `You already have all of the remaining stock in your cart`,
      CANNOT_FIND_VARIANT: 'There was an error adding this product to cart.',
      GENERIC: 'There was an error adding this product to cart.',
      NOT_ENOUGH_IN_STOCK: `Unfortunately, there isn't enough of this product available`,
      SOLD_OUT: `Unfortunately, this product is sold out.`,
    }

    this.description = error?.description ?? ''
    this.status = error?.status ?? null

    /**
     * @type {PushCart}
     * @public
     */
    this.pushCart = getElement('push-cart')
  }

  render (message) {
    const clone =
      this.pushCart.els['error-template'].element.content.cloneNode(true)
    const text = getElement(this.selectors.text, {
      context: clone,
    })

    text.textContent = message

    this.pushCart.els.errors.element.appendChild(clone)

    return this
  }

  get addedMoreThanExists () {
    return this.description.slice(0, 18) === "You can't add more"
  }

  get alreadyHaveAllInCart () {
    return this.description.slice(0, 4) === 'All '
  }

  get genericError () {
    return this.error?.errors?.toLowerCase() === ' error'
  }

  get soldOut () {
    return this.description.slice(0, 13) === "The product '"
  }

  get cannotFindVariant () {
    return this.description.toLowerCase() === 'cannot find variant'
  }

  get selectors () {
    return {
      item: '[data-push-cart-errors-item]',
      text: '[data-push-cart-errors-text]',
      close: '[data-push-cart-errors-close]',
    }
  }
}

export class PushCart extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['error-template', 'errors']
  }

  _loading = false

  get loading () {
    return this._loading
  }

  set loading (val) {
    this._loading = val

    this.classList.toggle(this.loadingClass, val)
    this.setAttribute('aria-busy', val)
  }

  constructor () {
    super()

    this.sections = window.BAO.sectionRenderer.pushCartSections

    if (this.drawer == null) {
      this.throwError('needs to be used within a <site-drawer> element')
    }
  }

  setupListeners () {
    super.setupListeners()

    this.onBeforeCartAdd = this.onBeforeCartAdd.bind(this)
    this.onCartChanged = this.onCartChanged.bind(this)
    this.onAfterCartAdd = this.onAfterCartAdd.bind(this)
    this.onBeforeDiscountUpdate = this.onBeforeDiscountUpdate.bind(this)
    this.onAfterDiscountUpdate = this.onAfterDiscountUpdate.bind(this)

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.BEFORE_ADD,
      this.onBeforeCartAdd,
    )
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.BEFORE_MULTIPLE_ADD,
      this.onBeforeCartAdd,
    )
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.BEFORE_ADD_FROM_FORM,
      this.onBeforeCartAdd,
    )
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.BEFORE_UPDATE_CART_DISCOUNTS,
      this.onBeforeDiscountUpdate,
    )
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.CHANGED,
      this.onCartChanged,
    )
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.AFTER_ADD,
      this.onAfterCartAdd,
    )
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.AFTER_MULTIPLE_ADD,
      this.onAfterCartAdd,
    )
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.AFTER_ADD_FROM_FORM,
      this.onAfterCartAdd,
    )
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.AFTER_UPDATE_CART_DISCOUNTS,
      this.onAfterDiscountUpdate,
    )
  }

  disconnectedCallback () {
    super.disconnectedCallback()

    window.BAO.eventBus.removeEventListener(
      window.BAO.EVENTS.PUSH_CART.BEFORE_ADD,
      this.onBeforeCartAdd,
    )

    window.BAO.eventBus.removeEventListener(
      window.BAO.EVENTS.PUSH_CART.AFTER_ADD,
      this.onAfterCartAdd,
    )
  }

  onBeforeDiscountUpdate (event) {
    this.loading = true
  }

  onAfterDiscountUpdate (event) {
    if ('sections' in event.detail.state) {
      const original = this.sections

      this.sections = original.map((section) => ({
        ...section,
        selectors: section.selectors.filter(
          (selector) => selector !== '[data-push-cart-el="discounts"]',
        ),
      }))

      try {
        this.renderSections(event.detail.state.sections)
      } finally {
        this.sections = original
      }
    }

    this.loading = false
  }

  onBeforeCartAdd () {
    if (this.cartType === 'drawer') {
      window.drawers.activeDrawerKey = 'cart'
    }

    this.loading = true
  }

  onAfterCartAdd (event) {
    if ('sections' in event.detail.state) {
      this.renderSections(event.detail.state.sections)
    }

    this.loading = false

    window.BAO.eventBus.dispatchEvent(
      window.BAO.EVENTS.PUSH_CART.AFTER_RERENDER,
      {
        state: event.detail.state,
      },
    )

    const type = event.detail.form?.dataset?.nostoType

    if (type !== 'recommendations') return

    const placementId = event.detail.form?.dataset?.nostoPlacementId
    const recoProductId = event.detail.state?.id

    if (!placementId || recoProductId == null) return

    try {
      window.nostojs((api) => {
        api.recommendedProductAddedToCart(recoProductId, placementId)
      })
    } catch (error) {
      console.error('NostoJS API call failed:', error)
    }
  }

  onCartChanged (e) {
    const { state } = e.detail
    if (!state) return
    if (!state.sections) return

    if (!('push-cart-count' in state.sections)) return

    const newCartTotal = parseInt(
      this.getCount(state.sections['push-cart-count']),
    )

    this.externalEls.drawer?.element.classList.toggle(
      'drw-Drawer-pushCartEmpty',
      newCartTotal === 0,
    )

    if (newCartTotal === 0) {
      this.externalEls.checkout?.element.setAttribute(
        'disabled',
        newCartTotal === 0,
      )
    } else {
      this.externalEls.checkout?.element.removeAttribute('disabled')
    }

    const original = this.sections

    this.sections = original.map((section) => ({
      ...section,
      selectors: section.selectors.filter(
        (selector) => selector !== '[data-push-cart-el="discounts"]',
      ),
    }))

    try {
      this.renderSections(event.detail.state.sections)
    } finally {
      this.sections = original
    }

    window.BAO.eventBus.dispatchEvent(
      window.BAO.EVENTS.PUSH_CART.AFTER_RERENDER,
      {
        state: e.detail.state,
      },
    )

    this.loading = false
  }

  /**
   * @param {string} html
   *
   * @returns {string}
   */
  getCount (html) {
    const DOM = window.BAO.sectionRenderer.convertToDOM(html)

    return DOM.querySelector('#push-cart-count').textContent
  }

  /**
   * @param {Response} res
   *
   * @returns {void}
   */
  async handleError (res) {
    this.loading = false

    return PushCartError.handle(await res.json())
  }

  get drawer () {
    return this.closest('[is="site-drawer"]')
  }

  get loadingClass () {
    return this.getAttribute('loading-class')
  }

  get cartType () {
    return this.getAttribute('cart-type')
  }
}

if (!customElements.get('push-cart')) {
  customElements.define('push-cart', PushCart)
}
